/**
 * Re-authentication for the idle lock — `docs/05-admin/02` §2.3.
 *
 * The lock asks for the password, and a code when the server asks for one: this runs the **real**
 * sign-in against the API rather than comparing anything locally. Since the owner's 2026-10-30
 * directive, two-factor is asked only of an admin who enrolled one (and of nobody while it is
 * switched off), so the password step may hand back the token pair itself. Two consequences worth
 * being deliberate about:
 *
 *   - **A successful unlock mints a fresh token pair.** That is not a side effect to work around,
 *     it is the point — the session that sat idle is replaced by one that did not.
 *   - **A failure leaves the existing session untouched.** Getting your own password wrong while
 *     unlocking must not sign you out; the lock stays up and says what happened.
 *
 * Returns `null` on success, or the message to show. It never throws — every failure here is
 * something to render inside the overlay.
 */
import { apiFetch, errorMessage } from '../api/client';
import { tokenStore } from './tokens';
import { adoptSession } from './session';
import type { components } from '../../api/generated/schema';

type LoginOut = components['schemas']['AdminLoginOut'];
type TokenOut = components['schemas']['AdminTokenOut'];

export async function reauthenticate(email: string, password: string, code: string): Promise<string | null> {
  try {
    const login = await apiFetch<LoginOut | TokenOut>('/v1/auth/admin/login', {
      method: 'POST',
      body: { email, password },
    });
    let tokens: TokenOut;
    if ('access_token' in login) {
      tokens = login;
    } else if (code.trim() === '') {
      /* The server wants the second factor and the box is empty: say so rather than sending a
         blank code that would count as a failed attempt against the lockout. */
      return 'Enter the code from your authenticator app.';
    } else {
      tokens = await apiFetch<TokenOut>('/v1/auth/admin/totp', {
        method: 'POST',
        body: { mfa_token: login.mfa_token, totp_code: code.trim() },
      });
    }
    tokenStore.write({ access: tokens.access_token, refresh: tokens.refresh_token });
    /* Re-reading `me` is not ceremony: scopes can have changed while the console sat idle, and
       coming back to a nav shaped by the old grant is exactly the stale state the lock exists to
       prevent. */
    await adoptSession();
    return null;
  } catch (error) {
    return errorMessage(error);
  }
}
